import React, { useEffect, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'

import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'

const ArticleCategoryEditContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Edit Article Category">
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary"
                        to="/article-categories"
                        data-testid="back-to-article-categories"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Article Categories
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const ArticleCategoryEdit = ({ ArticleCategoryForm, location: { pathname } }) => {
    const { client, getResource } = useClient()
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()

    const [articleCategory, setArticleCategory] = useState()

    const fetchArticleCategory = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'article-categories', {
                id: pathname,
            })

            setArticleCategory(data)
            setLoading(false)

            if (response.status === 404) {
                setRedirect('/article-categories')
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect('/article-categories')
        }
    }

    useEffect(() => {
        fetchArticleCategory()
    }, [])

    const schema = getSchemaFromResource(getResource('article-categories'))

    const onSubmit = async (data, actions) => {
        try {
            const response = await client(UPDATE, 'article-categories', {
                id: pathname,
                data: {
                    ...data,
                    parent: data?.parent?.value || null,
                },
            })

            if (response.data.violations && response.data.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                response.data.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            } else {
                actions.setSubmitting(false)
                toast.success('Article Category successfully updated!')
            }
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            actions.setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <ArticleCategoryEditContainer>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </ArticleCategoryEditContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/article-categories"
                message="The article category you're looking for could not be found"
                buttonLabel="Go to Article Categories"
            />
        )
    }

    return (
        <ArticleCategoryEditContainer>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>{articleCategory.name || 'Article Category'} | Edit | Peracto</title>
            </Helmet>

            <ArticleCategoryForm values={articleCategory} onSubmit={onSubmit} schema={schema} />
        </ArticleCategoryEditContainer>
    )
}

export default ArticleCategoryEdit
